import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';

export interface Company {
  id: string;
  name: string;
  created_at: string;
  has_access: boolean;
  tier?: 'free' | 'premium' | 'elite';
}

export interface CompanyProfile {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  logo?: string;
}

export const useCompany = () => {
  const { companyId } = useAuth();
  const { toast } = useToast();
  const [company, setCompany] = useState<Company | null>(null);
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debug logging for useCompany
  console.log('=== useCompany Hook Debug ===');
  console.log('companyId from useAuth:', companyId);

  const fetchCompany = async () => {
    if (!companyId) {
      setLoading(false);
      setError('Şirket ID bulunamadı. Lütfen yöneticinizle iletişime geçin.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (companyError) {
        throw companyError;
      }

      setCompany(companyData);

      setCompanyProfile({
        id: companyData.id,
        name: companyData.name,
        address: companyData.address || '',
        phone: companyData.phone || '',
        email: companyData.email || '',
        website: companyData.website || '',
      });

    } catch (err: any) {
      const errorMessage = err.message || 'Şirket bilgileri yüklenirken hata oluştu';
      setError(errorMessage);
      console.error('Error fetching company:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateCompany = async (updates: Partial<CompanyProfile>) => {
    if (!companyId) {
      toast({
        title: "Hata",
        description: "Şirket ID bulunamadı",
        variant: "destructive",
      });
      return false;
    }

    try {
      const updateData: Record<string, any> = {};
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.address !== undefined) updateData.address = updates.address;
      if (updates.phone !== undefined) updateData.phone = updates.phone;
      if (updates.email !== undefined) updateData.email = updates.email;
      if (updates.website !== undefined) updateData.website = updates.website;

      const { error } = await supabase
        .from('companies')
        .update(updateData)
        .eq('id', companyId);

      if (error) {
        throw error;
      }

      // Update local state
      if (company) {
        setCompany({ ...company, name: updates.name || company.name });
      }
      if (companyProfile) {
        setCompanyProfile({ ...companyProfile, ...updates });
      }

      toast({
        title: "Başarılı",
        description: "Şirket bilgileri güncellendi",
      });

      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Şirket bilgileri güncellenirken hata oluştu';
      toast({
        title: "Hata",
        description: errorMessage,
        variant: "destructive",
      });
      console.error('Error updating company:', err);
      return false;
    }
  };

  useEffect(() => {
    void fetchCompany();
  }, [companyId]);

  return {
    company,
    currentCompany: company, // Add currentCompany alias for backward compatibility
    companyProfile,
    loading,
    error,
    updateCompany,
    refetch: fetchCompany
  };
};
